import { takeLatest, call, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';

import { plantasApi } from 'services/apiDigitalTicket/plantas';
import { fleetApi } from 'services/apiDigitalTicket/fleet';
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';
import { errorMessagersList } from 'helpers/errorMessagers';
import PATH from 'constants/basePath';

import * as PlantasActions from './actions';
import { PlantasTypes } from './types';

function* list({
  payload,
}: ReturnType<typeof PlantasActions.listSeatmapsRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando plantas' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(plantasApi.list, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });
    if (response.status === 200) {
      yield put(
        PlantasActions.listSeatmapsSuccess({
          seatmapsList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10) || response.data.length,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response?.data?.internalCode;
    const message = errorMessagersList(internalCode);
    console.error('plantas - list - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
        status: error.response?.status,
      }),
    );
    yield put(PlantasActions.listSeatmapsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
}: ReturnType<typeof PlantasActions.getSeatmapRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const response = yield call(plantasApi.get, {
      params: { id: payload.id },
    });
    if (response.status === 200) {
      yield put(
        PlantasActions.getSeatmapSuccess({ seatmapData: response.data }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response?.data?.internalCode;
    const message = errorMessagersList(internalCode);
    console.error('plantas - get - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(PlantasActions.getSeatmapFailure());
    yield put(DialogsActions.setClose());
  }
}

function* create({
  payload,
}: ReturnType<typeof PlantasActions.createSeatmapRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando planta' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(plantasApi.create, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });
    if (response.status === 200) {
      yield put(
        PlantasActions.createSeatmapSuccess({ seatmapData: response.data }),
      );

      // Assign selected cars to the new seatmap
      const { carIds } = payload as any;
      if (carIds && carIds.length > 0) {
        const newSeatmapId = response.data.id;
        for (const carId of carIds) {
          try {
            const carResponse = yield call(fleetApi.get, { params: { id: carId, transportOperatorId: transportOperator.id } });
            const car = carResponse.data;
            yield call(fleetApi.update, { data: { ...car, carId: car.id, picture: '', seatmapId: newSeatmapId, transportOperatorId: transportOperator.id } });
          } catch (carError) {
            console.error(`plantas - create - failed to assign car ${carId}:`, carError);
          }
        }
      }

      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Planta criada com sucesso',
        }),
      );
      yield put(push(PATH.PLANTAS.BASE_URL));
    }
  } catch (error) {
    const internalCode: string =
      error.response?.data?.internalCode;
    const message = errorMessagersList(internalCode);
    console.error('plantas - create - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(PlantasActions.createSeatmapFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof PlantasActions.updateSeatmapRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Salvando planta' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(plantasApi.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });
    if (response.status === 200) {
      yield put(
        PlantasActions.updateSeatmapSuccess({ seatmapData: response.data }),
      );

      // Sync car assignments
      const { carIdsToAdd, carIdsToRemove, id: seatmapId } = payload as any;
      if (carIdsToAdd && carIdsToAdd.length > 0) {
        for (const carId of carIdsToAdd) {
          try {
            const carResponse = yield call(fleetApi.get, { params: { id: carId, transportOperatorId: transportOperator.id } });
            const car = carResponse.data;
            yield call(fleetApi.update, { data: { ...car, carId: car.id, picture: '', seatmapId, transportOperatorId: transportOperator.id } });
          } catch (carError) {
            console.error(`plantas - update - failed to assign car ${carId}:`, carError);
          }
        }
      }
      if (carIdsToRemove && carIdsToRemove.length > 0) {
        for (const carId of carIdsToRemove) {
          try {
            const carResponse = yield call(fleetApi.get, { params: { id: carId, transportOperatorId: transportOperator.id } });
            const car = carResponse.data;
            yield call(fleetApi.update, { data: { ...car, carId: car.id, picture: '', seatmapId: null, transportOperatorId: transportOperator.id } });
          } catch (carError) {
            console.error(`plantas - update - failed to unassign car ${carId}:`, carError);
          }
        }
      }

      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Planta atualizada com sucesso',
        }),
      );
      yield put(push(PATH.PLANTAS.BASE_URL));
    }
  } catch (error) {
    const internalCode: string =
      error.response?.data?.internalCode;
    const message = errorMessagersList(internalCode);
    console.error('plantas - update - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(PlantasActions.updateSeatmapFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof PlantasActions.deleteSeatmapRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo planta' }));
    const response = yield call(plantasApi.delete, {
      data: { id: payload.id },
    });
    if (response.status === 200) {
      yield put(PlantasActions.deleteSeatmapSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Planta excluída com sucesso',
        }),
      );
      yield put(PlantasActions.listSeatmapsRequest());
    }
  } catch (error) {
    const internalCode: string =
      error.response?.data?.internalCode;
    const message = errorMessagersList(internalCode);
    console.error('plantas - remove - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(PlantasActions.deleteSeatmapFailure());
    yield put(DialogsActions.setClose());
  }
}

export const plantasSagas = [
  takeLatest(PlantasTypes.LIST_SEATMAPS_REQUEST, list),
  takeLatest(PlantasTypes.GET_SEATMAP_REQUEST, get),
  takeLatest(PlantasTypes.CREATE_SEATMAP_REQUEST, create),
  takeLatest(PlantasTypes.UPDATE_SEATMAP_REQUEST, update),
  takeLatest(PlantasTypes.DELETE_SEATMAP_REQUEST, remove),
];
